import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import classnames from 'classnames';

import { COLORS, ROUTES } from 'shared/constants';
import { PermissionEnum } from 'shared/enums';
import { useAsyncEffect, useDebounce, useMedia, useNavigate, useReaction, useUrlParams, useStore, useEffect } from 'shared/hooks';
import { LayoutBody, LayoutContent } from 'shared/layout';
import { ApplicationModule } from 'shared/modules';
import { UiDataBoundary } from 'shared/uikit';
import { UiButton, UiCalendar, UiIcon, UiSelect } from 'shared/uikit-new';
import { calendarItemsQuery } from 'shared/queries';
import { CalendarItemModel } from 'shared/models';
import { Notifier, Storage, toDate } from 'shared/utilities';
import { MdTaskTrackerTask } from 'shared/modals';
import { DateType } from 'shared/types';

import { CALENDAR_PAGE } from './constants/CALENDAR_PAGE';
import { CalendarPageDatePagination } from './components/CalendarPageDatePagination';
import { CalendarPageYear } from './components/CalendarPageYear';
import { CalendarPageDays } from './components/CalendarPageDays';
import { CalendarPageMonth } from './components/CalendarPageMonth';
import { CalendarPageCreatingPopout } from './components/CalendarPageCreatingPopout';
import { CalendarPageCalendarItemEdit } from './components/CalendarPageCalendarItemEdit';
import { CalendarPageFilter } from './components/CalendarPageFilter';

import { Store } from './store';

import './styles.scss';

export const CalendarPage = observer(function CalendarPage() {
    const state = useStore(() => ({
        date: Store.date,
        calendarItems: [] as CalendarItemModel[],
    }));

    const onUpdateDate = (date: DateType) => {
        if (state.date.month() != date.month() || state.date.format("YYYY") != date.format("YYYY")) {
            state.date = date;
        }
    }

    const urlParams = useUrlParams({
        date: undefined as string | undefined,
    });

    const navigate = useNavigate();
    const { isDesktop } = useMedia();

    const loadCalendarPreview = useCallback(
        async () => {
            const response = await calendarItemsQuery({
                dateFrom: state.date.clone().startOf('month').startOf('week').format('YYYY-MM-DD'),
                dateTo: state.date.clone().endOf('month').endOf('week').format('YYYY-MM-DD'),
                withBirthdays: 0,
            });

            if (response.isSuccess && response.data) {
                state.set('calendarItems', response.data.items.map(item => new CalendarItemModel(item)));
            }
        },
        [state]
    )

    useReaction(async () => {
        const storeDate = Store.date;

        const noWeekends = await Storage.get('calendar.noWeekends', false);
        Store.filter.set('noWeekends', noWeekends);

        if (Store.filter.noWeekends) {
            if (storeDate.day() == 6 || storeDate.day() == 0) {
                switch (storeDate.day()) {
                    case 6:
                        navigate(null, {
                            date: storeDate.subtract(1, 'day').format('YYYY-MM-DD'),
                        });
                        break;
                    case 0:
                        navigate(null, {
                            date: storeDate.add(1, 'day').format('YYYY-MM-DD'),
                        });
                        break;
                }
                return
            }
        }

        if (urlParams.date === storeDate.format('YYYY-MM-DD')) {
            return;
        }

        navigate(null, {
            date: storeDate.format('YYYY-MM-DD'),
        });
    }, () => Store.date);

    useEffect(() => {loadCalendarPreview(); }, [state.date, loadCalendarPreview]);

    const skipFirstItemsChange = React.useRef(true);

    useReaction(
        () => {
            if (skipFirstItemsChange.current) {
                skipFirstItemsChange.current = false;
                return;
            }
            setTimeout(() => {
                loadCalendarPreview();
            }, 100)
        },
        () => Store.calendarItems,
        false,
    );

    useReaction(() => setTimeout(() => {
        Storage.set('calendar.noWeekends', Store.filter.noWeekends);

        if (Store.filter.noWeekends && (Store.date.day() == 6 || Store.date.day() == 0)) {
            switch (Store.date.day()) {
                case 6:
                    Store.set('date', Store.date.subtract(1, 'day'));
                    break;
                case 0:
                    Store.set('date', Store.date.add(1, 'day'));
                    break;
            }
        }
    }, 100), () => Store.filter.noWeekends);

    useAsyncEffect(async () => {
        if (!ApplicationModule.user.can(PermissionEnum.WebCalendarAndReservation)) {
            navigate(ROUTES.HOME());
            return;
        }

        ApplicationModule.setTitle('Календарь');
        if (urlParams['date']) {
            Store.set('date', toDate(urlParams['date']));
        }
    }, [navigate]);

    const load = useDebounce(async () => {
        let dateFrom = Store.date.startOf('day');
        let dateTo = Store.date.startOf('day').add(1, 'day');

        switch (Store.view) {
            case 'days':
                dateFrom = Store.date.subtract(1, 'day');
                dateTo = Store.date.add(2, 'day');
                break;
            case 'week':
                dateFrom = Store.date.startOf('week');
                dateTo = Store.date.startOf('week').add(7, 'day');
                break;
            case 'month':
                dateFrom = Store.date.startOf('month').startOf('week');
                dateTo = Store.date.endOf('month').endOf('week').add(1, 'day');
                break;
            case 'year':
                dateFrom = Store.date.startOf('year');
                dateTo = Store.date.startOf('year').add(1, 'year');
                break;
        }

        const response = await calendarItemsQuery({
            dateFrom: dateFrom.format('YYYY-MM-DD'),
            dateTo: dateTo.format('YYYY-MM-DD'),
        });

        if (response.isSuccess && response.status !== 'error' && response.data) {
            Store.set(
                'calendarItems',
                response.data.items.map(item => new CalendarItemModel(item)),
            );
        } else if (response.errorMessage) {
            Store.set('calendarItems', []);
            Notifier.message('Ошибка', response.errorMessage);
        }

        Store.set('isLoading', false);
    }, 600);

    useReaction(
        async () => {
            Store.set('calendarItems', []);
            Store.set('isLoading', true);
            load();
        },
        () => [Store.date, Store.view],
    );

    if (!ApplicationModule.user.outlookLogin) {
        return (
            <LayoutBody>
                <LayoutContent>
                    <UiDataBoundary
                        isError
                        errorMessage={'Подключите календарь Outlook в личном кабинете'}
                        actionText={'Перейти в личный кабинет'}
                        onAction={() => navigate(ROUTES.PROFILE())}
                    />
                </LayoutContent>
            </LayoutBody>
        );
    }

    return (
        <LayoutBody
            className={classnames('calendar-page', `calendar-page--${Store.view}`, {
                'calendar-page--no-weekends': Store.filter.noWeekends,
            })}
        >
            <LayoutContent className={'calendar-page__layout-content'}>
                <div className="calendar-page-header">
                    <UiButton
                        template={isDesktop ? 'base' : 'icon'}
                        onClick={() => Store.reset('date')}
                        style={{
                            backgroundColor: [COLORS._F3F5F8, COLORS._C6CAD1],
                            color: [COLORS._2C2F35, COLORS._2C2F35],
                        }}
                    >
                        <UiIcon.CalendarCheck size={20} />
                        {isDesktop && <span>Сегодня</span>}
                    </UiButton>
                    <CalendarPageDatePagination />
                    <div className="calendar-page-header__filters">
                        <UiSelect
                            slots={{
                                control: ({ onClick, isOpened, item }) => (
                                    <UiButton
                                        onClick={onClick}
                                        style={{
                                            backgroundColor: [COLORS._F3F5F8, COLORS._C6CAD1],
                                            color: [COLORS._2C2F35, COLORS._2C2F35],
                                        }}
                                    >
                                        <span>{item?.name || 'Не выбрано'}</span>
                                        {isOpened ? <UiIcon.ChevronUp size={16} /> : <UiIcon.ChevronDown size={16} />}
                                    </UiButton>
                                ),
                            }}
                            styles={{
                                items: {
                                    right: 0,
                                    left: 'auto',
                                    minWidth: 200,
                                },
                            }}
                            items={Object.values(CALENDAR_PAGE.VIEWS)}
                            onChange={Store.handleChange}
                            name={'view'}
                            value={Store.view}
                        />
                        <CalendarPageFilter />
                    </div>
                </div>
                <div className="calendar-page__inner">
                    <div className="calendar-page__aside">
                        <CalendarPageCreatingPopout
                            date={Store.date.set('hours', CALENDAR_PAGE.HOURS[0]).set('minutes', 0)}
                            render={({ onClick }) => (
                                <UiButton className={'calendar-page__create'} onClick={onClick}>
                                    <UiIcon.Plus size={20} />
                                    <span>Создать</span>
                                </UiButton>
                            )}
                        />
                        <UiCalendar
                            value={Store.date.format()}
                            onChange={({ value }) => {
                                Store.set('date', toDate(value));
                            }}
                            items={state.calendarItems.map(calendarItem => toDate(calendarItem.startAt).format('YYYY-MM-DD'))}
                            disablesDays={Store.filter.noWeekends ? [6, 0] : null}
                            onUpdateDate={onUpdateDate}
                        />
                    </div>
                    <div
                        className={classnames('calendar-page__content', {
                            'calendar-page__content--loading': Store.isLoading,
                        })}
                    >
                        {['week', 'days', 'day'].includes(Store.view) && <CalendarPageDays />}
                        {Store.view === 'month' && <CalendarPageMonth />}
                        {Store.view === 'year' && <CalendarPageYear />}
                    </div>
                </div>
            </LayoutContent>
            <CalendarPageCalendarItemEdit />
            <MdTaskTrackerTask
                isOpened={Store.taskTrackerTask !== null}
                taskTrackerTask={Store.taskTrackerTask || undefined}
                onSaved={(taskTrackerTask) => {
                    load();
                    Store.set('taskTrackerTask', { id: taskTrackerTask.id });
                }}
                onDeleted={() => {
                    load();
                    Store.set('taskTrackerTask', null);
                }}
                onClose={() => Store.set('taskTrackerTask', null)}
            />
        </LayoutBody>
    );
});
